import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Search, Package, FileText, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { usePolicyTemplates } from "@/hooks/use-policy-templates";
import { useTemplateStacks } from "@/hooks/use-template-stacks";
import { TemplateCard } from "@/components/templates/TemplateCard";
import { TemplateStackCard } from "@/components/templates/TemplateStackCard";
import { StackPreviewDialog } from "@/components/templates/StackPreviewDialog";
import { TemplatePreviewDialog } from "@/components/templates/TemplatePreviewDialog";
import { copyTemplateToPolicy, createPoliciesFromStack } from "@/lib/template-utils";
import { useToast } from "@/hooks/use-toast";
import type { PolicyTemplate } from "@/lib/template-utils";
import type { TemplateStack } from "@/lib/template-utils";
import { Skeleton } from "@/components/ui/skeleton";

const Templates = () => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState<'all' | 'global' | 'internal'>('all');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedFramework, setSelectedFramework] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'stacks' | 'templates'>('stacks');
  
  // Dialog states
  const [previewStack, setPreviewStack] = useState<TemplateStack | null>(null);
  const [previewTemplate, setPreviewTemplate] = useState<PolicyTemplate | null>(null);

  // Fetch templates with filters
  const { templates, loading: templatesLoading, error: templatesError } = usePolicyTemplates({
    templateType: selectedType === 'all' ? undefined : selectedType,
    category: selectedCategory === 'all' ? undefined : selectedCategory,
    complianceFramework: selectedFramework === 'all' ? undefined : selectedFramework,
    searchQuery: searchQuery || undefined,
  });

  // Fetch stacks with filters
  const { stacks, loading: stacksLoading, error: stacksError } = useTemplateStacks({
    category: selectedCategory === 'all' ? undefined : selectedCategory,
    complianceFramework: selectedFramework === 'all' ? undefined : selectedFramework,
  });

  const handleUseTemplate = async (template: PolicyTemplate) => {
    if (!profile?.company_id || !profile?.id) {
      toast({
        title: "Error",
        description: "Must be logged in to use templates",
        variant: "destructive"
      });
      return;
    }

    try {
      const policyId = await copyTemplateToPolicy(template.id, profile.company_id, profile.id);
      if (policyId) {
        toast({
          title: "Success",
          description: "Policy created from template"
        });
        navigate(`/policy/${policyId}`);
      }
    } catch (error) {
      console.error('Error using template:', error);
      toast({
        title: "Error",
        description: "Failed to create policy from template",
        variant: "destructive"
      });
    }
  };

  const handlePreviewTemplate = (template: PolicyTemplate) => {
    setPreviewTemplate(template);
  };

  const handleViewStack = (stack: TemplateStack) => {
    setPreviewStack(stack);
  };

  const handleCreateFromStack = async (selectedTemplateIds: string[]) => {
    if (!profile?.company_id || !profile?.id || !previewStack) {
      return;
    }

    try {
      const policyIds = await createPoliciesFromStack(
        previewStack.id,
        profile.company_id,
        profile.id,
        selectedTemplateIds
      );

      if (policyIds && policyIds.length > 0) {
        toast({
          title: "Success",
          description: `Created ${policyIds.length} policies from stack`
        });
        // Navigate to first policy
        navigate(`/policy/${policyIds[0]}`);
      }
    } catch (error) {
      console.error('Error creating from stack:', error);
      toast({
        title: "Error",
        description: "Failed to create policies from stack",
        variant: "destructive"
      });
    }
  };

  const allCategories = Array.from(new Set([...templates.map(t => t.category).filter(Boolean)]));
  const allFrameworks = Array.from(new Set([...templates.map(t => t.compliance_framework).filter(Boolean)]));

  // Show loading state if either stacks or templates are loading
  const isLoading = stacksLoading || templatesLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="space-y-2">
          <Skeleton variant="shimmer" className="h-8 w-64" />
          <Skeleton variant="shimmer" className="h-5 w-96" />
        </div>

        {/* Search and Filters Skeleton */}
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              {/* Search Bar Skeleton */}
              <Skeleton variant="shimmer" className="h-10 w-full" />

              {/* Filters Row Skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton variant="shimmer" className="h-3 w-20" />
                    <Skeleton variant="shimmer" className="h-10 w-full" />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs Skeleton */}
        <div className="space-y-4">
          <div className="flex space-x-4">
            <Skeleton variant="shimmer" className="h-10 w-32" />
            <Skeleton variant="shimmer" className="h-10 w-36" />
          </div>

          {/* Template Cards Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 space-y-2">
                        <Skeleton variant="shimmer" className="h-5 w-3/4" />
                        <Skeleton variant="shimmer" className="h-4 w-full" />
                        <Skeleton variant="shimmer" className="h-4 w-2/3" />
                      </div>
                      <Skeleton variant="shimmer" className="h-5 w-12 rounded-full" />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <Skeleton variant="shimmer" className="h-4 w-4 rounded" />
                        <Skeleton variant="shimmer" className="h-4 w-20" />
                      </div>
                      <div className="flex items-center gap-2">
                        <Skeleton variant="shimmer" className="h-4 w-4 rounded" />
                        <Skeleton variant="shimmer" className="h-4 w-24" />
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Skeleton variant="shimmer" className="h-4 w-1/4" />
                      <div className="flex flex-wrap gap-1">
                        {[...Array(2)].map((_, j) => (
                          <Skeleton key={j} variant="shimmer" className="h-5 w-16 rounded-full" />
                        ))}
                      </div>
                    </div>
                    
                    <div className="flex gap-2 pt-2">
                      <Skeleton variant="shimmer" className="h-9 flex-1" />
                      <Skeleton variant="shimmer" className="h-9 w-16" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h1 className="text-3xl font-medium text-foreground">Template Marketplace</h1>
        <p className="text-muted-foreground">
          Choose from pre-built compliance stacks or individual templates to jump-start your policy creation.
        </p>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
              <Input
                placeholder="Search templates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Filters Row */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Template Type Filter */}
              <div className="space-y-2">
                <Label className="text-xs">Template Type</Label>
                <Select value={selectedType} onValueChange={(value) => setSelectedType(value as any)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="global">Global</SelectItem>
                    <SelectItem value="internal">Internal</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Category Filter */}
              <div className="space-y-2">
                <Label className="text-xs">Category</Label>
                <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {allCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Framework Filter */}
              <div className="space-y-2">
                <Label className="text-xs">Compliance Framework</Label>
                <Select value={selectedFramework} onValueChange={setSelectedFramework}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Frameworks</SelectItem>
                    {allFrameworks.map(fw => (
                      <SelectItem key={fw} value={fw}>{fw}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery("");
                    setSelectedType('all');
                    setSelectedCategory('all');
                    setSelectedFramework('all');
                  }}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for Stacks vs Templates */}
      <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="stacks" className="gap-2">
            <Package className="h-4 w-4" />
            Template Stacks
          </TabsTrigger>
          <TabsTrigger value="templates" className="gap-2">
            <FileText className="h-4 w-4" />
            Individual Templates
          </TabsTrigger>
        </TabsList>

        {/* Stacks Tab */}
        <TabsContent value="stacks" className="space-y-4">
          {stacksLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(6)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-start gap-3">
                        <Skeleton variant="shimmer" className="h-12 w-12 rounded-lg" />
                        <div className="flex-1 space-y-2">
                          <Skeleton variant="shimmer" className="h-5 w-3/4" />
                          <Skeleton variant="shimmer" className="h-4 w-full" />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Skeleton variant="shimmer" className="h-4 w-1/4" />
                        <div className="flex flex-wrap gap-1">
                          {[...Array(3)].map((_, j) => (
                            <Skeleton key={j} variant="shimmer" className="h-5 w-16 rounded-full" />
                          ))}
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Skeleton variant="shimmer" className="h-4 w-1/3" />
                        <div className="space-y-1">
                          {[...Array(3)].map((_, j) => (
                            <Skeleton key={j} variant="shimmer" className="h-4 w-full" />
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Skeleton variant="shimmer" className="h-9 w-20" />
                        <Skeleton variant="shimmer" className="h-9 w-16" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : stacksError ? (
            <div className="flex items-center justify-center py-12 text-destructive">
              <AlertCircle className="h-8 w-8 mr-2" />
              Failed to load template stacks
            </div>
          ) : stacks.length === 0 ? (
            <div className="text-center py-12">
              <Package className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">No stacks found</h3>
              <p className="text-sm text-muted-foreground">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {stacks.map(stack => (
                <TemplateStackCard
                  key={stack.id}
                  stack={stack}
                  onView={handleViewStack}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-4">
          {templatesLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[...Array(9)].map((_, i) => (
                <Card key={i}>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 space-y-2">
                          <Skeleton variant="shimmer" className="h-5 w-3/4" />
                          <Skeleton variant="shimmer" className="h-4 w-full" />
                          <Skeleton variant="shimmer" className="h-4 w-2/3" />
                        </div>
                        <Skeleton variant="shimmer" className="h-5 w-12 rounded-full" />
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Skeleton variant="shimmer" className="h-4 w-4 rounded" />
                          <Skeleton variant="shimmer" className="h-4 w-20" />
                        </div>
                        <div className="flex items-center gap-2">
                          <Skeleton variant="shimmer" className="h-4 w-4 rounded" />
                          <Skeleton variant="shimmer" className="h-4 w-24" />
                        </div>
                        <div className="flex items-center gap-2">
                          <Skeleton variant="shimmer" className="h-4 w-4 rounded" />
                          <Skeleton variant="shimmer" className="h-4 w-16" />
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <Skeleton variant="shimmer" className="h-4 w-1/4" />
                        <div className="flex flex-wrap gap-1">
                          {[...Array(2)].map((_, j) => (
                            <Skeleton key={j} variant="shimmer" className="h-5 w-16 rounded-full" />
                          ))}
                        </div>
                      </div>
                      
                      <div className="flex gap-2 pt-2">
                        <Skeleton variant="shimmer" className="h-9 flex-1" />
                        <Skeleton variant="shimmer" className="h-9 w-16" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : templatesError ? (
            <div className="flex items-center justify-center py-12 text-destructive">
              <AlertCircle className="h-8 w-8 mr-2" />
              Failed to load templates
            </div>
          ) : templates.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold text-muted-foreground mb-2">No templates found</h3>
              <p className="text-sm text-muted-foreground">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templates.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onPreview={handlePreviewTemplate}
                  onUse={handleUseTemplate}
                  showUsageCount={template.template_type === 'internal'}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Dialogs */}
      <StackPreviewDialog
        stack={previewStack}
        open={!!previewStack}
        onOpenChange={(open) => !open && setPreviewStack(null)}
        onConfirm={handleCreateFromStack}
      />

      <TemplatePreviewDialog
        template={previewTemplate}
        open={!!previewTemplate}
        onOpenChange={(open) => !open && setPreviewTemplate(null)}
        onUse={handleUseTemplate}
      />
    </div>
  );
};

export default Templates;


